import React, { useRef, useState, useEffect } from 'react';
import { Type, GripHorizontal, Image as IconOnly, AlignLeft as IconText } from 'lucide-react';
import { useTextSize } from '../contexts/TextSizeContext';

const TextSizeControl = ({ textSize, onTextSizeChange }) => {
  const { tabLabelMode, setTabLabelMode } = useTextSize();
  const sizes = [
    { id: 'large', label: 'Large', icon: 'L' },
    { id: 'medium', label: 'Medium', icon: 'M' },
    { id: 'small', label: 'Small', icon: 'S' },
    { id: 'tiny', label: 'Tiny', icon: 'T' }
  ];

  // Draggable/Touchable floating panel
  const panelRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem('popupSettingOpen');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const onMouseMove = (e) => {
      if (!dragging) return;
      el.style.left = `${e.clientX - offset.x}px`;
      el.style.top = `${e.clientY - offset.y}px`;
    };
    const onTouchMove = (e) => {
      if (!dragging) return;
      const t = e.touches[0];
      el.style.left = `${t.clientX - offset.x}px`;
      el.style.top = `${t.clientY - offset.y}px`;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [dragging, offset]);

  useEffect(() => {
    localStorage.setItem('popupSettingOpen', String(isOpen));
  }, [isOpen]);

  return (
    <>
    {!isOpen && (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-lg text-gray-700 font-bold hover:bg-gray-50"
        title="開啟 Popup Setting"
      >
        T
      </button>
    )}
    {isOpen && (
    <div ref={panelRef} className="fixed bottom-4 right-4 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-56 select-none">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Type className="w-4 h-4 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Popup Setting</span>
          </div>
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onMouseDown={(e)=>{ const rect = panelRef.current.getBoundingClientRect(); setOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top }); setDragging(true);} }
            onMouseUp={()=>setDragging(false)}
            onTouchStart={(e)=>{ const rect = panelRef.current.getBoundingClientRect(); const t = e.touches[0]; setOffset({ x: t.clientX - rect.left, y: t.clientY - rect.top }); setDragging(true);} }
            onTouchEnd={()=>setDragging(false)}
            title="拖曳移動"
          >
            <GripHorizontal className="w-4 h-4 text-gray-500" />
          </button>
          <button
            className="ml-1 px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 text-gray-600"
            onClick={() => setIsOpen(false)}
            title="隱藏到 T 按鈕"
          >
            T
          </button>
        </div>

        {/* Text size */}
        <div className="mt-3">
          <div className="text-xs text-gray-600 mb-1">文字大小</div>
          <div className="flex flex-wrap gap-1">
            {sizes.map((size) => (
              <button
                key={size.id}
                onClick={() => onTextSizeChange(size.id)}
                className={`px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                  textSize === size.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {size.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab label mode */}
        <div className="mt-3">
          <div className="text-xs text-gray-600 mb-1">分頁標籤</div>
          <div className="flex items-center gap-2">
            <button
              onClick={()=>setTabLabelMode('icon')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${tabLabelMode==='icon' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              title="只顯示圖示"
            >
              <IconOnly className="w-3 h-3" /> icon only
            </button>
            <button
              onClick={()=>setTabLabelMode('icon_text')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${tabLabelMode==='icon_text' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              title="圖示 + 文字"
            >
              <IconText className="w-3 h-3" /> icon + text
            </button>
          </div>
        </div>
      </div>
    </div>
    )}
    </>
  );
};

export default TextSizeControl; 